// driver.js as this app uses it: one look and one set of buttons for the page
// tours (usePageTour) and for the spotlight behind an inline "?" (HelpMark),
// so the two cannot drift into different popovers for the same words.
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import "./tour.css";
import { TOURS } from "./tourSteps";

const TOUR_LOOK = {
  // theme-auto puts the popover on the dark tokens when the page is dark;
  // it is appended to <body>, outside the page's own theme-auto wrapper.
  popoverClass: "lineup-tour theme-auto",
  overlayColor: "#071F39",
  overlayOpacity: 0.55,
  stagePadding: 6,
  stageRadius: 12,
  smoothScroll: true,
};

const resolve = (value, ctx) => (typeof value === "function" ? value(ctx) : value);

// A step's copy for whoever is reading it. title and body may be functions of
// { demo }.
export const stepPopover = (step, ctx) => ({
  title: resolve(step.title, ctx),
  description: resolve(step.body, ctx),
});

const findStep = (name) => {
  const selector = `[data-tour="${name}"]`;
  return Object.values(TOURS).flat().find((step) => step.el === selector);
};

/**
 * Runs driver steps ({ element, popover }) and returns the driver, or null
 * when there are none. One step shows Done and the cross; more add Back, Next
 * and the "2 of 4" count. onDestroyed runs however it ends: Done, the cross,
 * a tap outside, or destroy().
 */
export function runTour(steps, { onDestroyed, ...options } = {}) {
  if (steps.length === 0) return null;
  const single = steps.length === 1;

  const tour = driver({
    steps,
    showProgress: !single,
    progressText: "{{current}} of {{total}}",
    nextBtnText: "Next",
    prevBtnText: "Back",
    doneBtnText: "Done",
    showButtons: single ? ["next", "close"] : ["next", "previous", "close"],
    ...TOUR_LOOK,
    ...options,
    onDestroyed,
  });
  tour.drive();
  return tour;
}

/**
 * Tour steps on their own, by data-tour name, for an inline "?": the same
 * dimmed page, ring and popover as the page tour.
 *
 * element is what a single step lights up when its own selector is not the
 * right thing to point at: the "?" sits in a section the caller can find, and
 * the same rule can be explained on a page with no such element at all (the
 * Super Dog legend on the lineup). Several steps each use their own selector,
 * and one whose element is not on screen is dropped, as in the page tour.
 */
export function spotlightSteps(names, { element, demo = false, onClose } = {}) {
  const found = names.map(findStep).filter(Boolean);
  const steps = found
    .map((step) => ({
      element: found.length === 1 && element ? element : step.el,
      popover: stepPopover(step, { demo }),
    }))
    .filter((step) => (typeof step.element === "string" ? document.querySelector(step.element) : step.element));

  return runTour(steps, { onDestroyed: onClose });
}
